import { fabric } from 'fabric';
import range from 'lodash/range';
import Vec2 from 'vec2';

import {
  CANVAS_OBJECT_TYPE_SUFFIX,
  SHAPE_ROTATION_DEGREES,
} from '~/core/constants';
import { drawingTool } from '~/core/engine/DrawingTool';
import { calculateIsoscelesTriangleSide } from '~/core/engine/DrawingTool/utils';
import { DIRECTION, isBumpOut } from '~/core/enums';
import {
  findArrowByEdgeId,
  findDrawingEdgeById,
  getInterceptPoint,
  isCorrectDirection,
  subtractVectors,
} from '~/core/helpers';
import {
  ArrowInfo,
  DrawingAppliance,
  DrawingEdge,
  DrawingShape,
  SkeletonSeg,
} from '~/core/models';
import { makeApplianceName } from '~/core/utils';
import { calculateEdgeConstraints } from '~/core/utils/drawingTool';

const adjustBumpOutSides = (change: number, edge: DrawingEdge): void => {
  if (edge.bumpOut) {
    edge.bumpOut.clockwiseRemainderLength += change;
    edge.bumpOut.counterClockwiseRemainderLength += change;
  }
};

export const adjustEdgeForDiagonals = (
  shape: DrawingShape,
  edge: DrawingEdge
): number => {
  if (!shape) {
    return 0;
  }
  const startCorner = shape.cornerMap[edge.startCornerId];
  const endCorner = shape.cornerMap[edge.endCornerId];

  return [startCorner, endCorner].reduce(
    (acc, point) =>
      !!point.diagonalLength && point.isVisibleInCanvas
        ? acc +
          calculateIsoscelesTriangleSide(point.angle, point.diagonalLength) //Math.sqrt(Math.pow(point.diagonalLength, 2) / 2)
        : acc,
    0
  );
};

const createNormalizedVector = (x: number, y: number): Vec2 => {
  const length = Math.sqrt(x * x + y * y);

  return new Vec2(length > 0 ? x / length : 0, length > 0 ? y / length : 0);
};

const handleInteriorArrow = (
  arrowInfo: ArrowInfo,
  shape: DrawingShape,
  whatIf: boolean
): void => {
  const skData = shape.skeleton.slice();

  if (!arrowInfo.interiorSkIndex || !arrowInfo.size) {
    return;
  }

  skData[arrowInfo.interiorSkIndex].depth = arrowInfo.size / 2;
  drawingTool.rebuildShapeFromNewSkeleton(skData, shape);

  if (!whatIf) {
    calculateEdgeConstraints(shape);
  }
};

const getEdgeAndArrow = (args: {
  arrowInfo: Omit<ArrowInfo, 'drawingEdgeId'> & {
    drawingEdgeId: string;
  };
  shape: DrawingShape;
}): { edge: DrawingEdge | undefined; arrow: ArrowInfo | undefined } => {
  const { arrowInfo, shape } = args;

  const edge = findDrawingEdgeById(arrowInfo.drawingEdgeId, shape);
  const arrow = edge?.id ? findArrowByEdgeId(edge.id, shape) : undefined;

  return { edge, arrow };
};

/**
 * This is for simple shape - Rectangle AND it's a change to an end edge
 */
const handleSimpleRectangle = (args: {
  arrowInfoSize: number;
  shape: DrawingShape;
  edge: DrawingEdge;
  diff: number;
  skData: SkeletonSeg[];
  dirVec: Vec2;
  vec2StartSkPt: Vec2;
  vec2EndSkPt: Vec2;
}): SkeletonSeg[] => {
  const {
    arrowInfoSize,
    shape,
    edge,
    diff,
    skData,
    dirVec,
    vec2StartSkPt,
    vec2EndSkPt,
  } = args;

  let newStartSkPt = new Vec2(vec2StartSkPt.x, vec2StartSkPt.y);
  let newEndSkPt = new Vec2(vec2EndSkPt.x, vec2EndSkPt.y);

  if (!isCorrectDirection(dirVec, DIRECTION.DOWN)) {
    dirVec.multiply(-1);
  }
  newStartSkPt = new Vec2(skData[0].pos.x, skData[0].pos.y).add(
    dirVec.multiply(diff * 0.5, true),
    true
  );
  newEndSkPt = new Vec2(skData[1].pos.x, skData[1].pos.y).add(
    dirVec.multiply(diff * 0.5, true),
    true
  );
  const depth = (arrowInfoSize + adjustEdgeForDiagonals(shape, edge)) * 0.5;

  return [
    { pos: newStartSkPt, depth },
    { pos: newEndSkPt, depth },
  ];
};

const handleEndEdge = (
  arrowInfo: ArrowInfo,
  shape: DrawingShape,
  edge: DrawingEdge,
  diff: number,
  skData: SkeletonSeg[],
  startSkId: number,
  vec2StartSkPt: Vec2,
  dirVec: Vec2
): SkeletonSeg[] => {
  if (!arrowInfo.size) {
    return skData;
  }

  const skLength = skData.length;
  const depth = (arrowInfo.size + adjustEdgeForDiagonals(shape, edge)) * 0.5;

  const { midSkIndex, nextSkIndex } =
    startSkId === skLength - 1
      ? { midSkIndex: skLength - 2, nextSkIndex: skLength - 3 }
      : { midSkIndex: startSkId + 1, nextSkIndex: startSkId + 2 };

  const midSkPt = new Vec2(skData[midSkIndex].pos.x, skData[midSkIndex].pos.y);
  const nextSkPt = new Vec2(
    skData[nextSkIndex].pos.x,
    skData[nextSkIndex].pos.y
  );

  const skTurn =
    (midSkPt.x - vec2StartSkPt.x) * (nextSkPt.y - vec2StartSkPt.y) -
      (midSkPt.y - vec2StartSkPt.y) * (nextSkPt.x - vec2StartSkPt.x) >
    0
      ? 1
      : -1;

  const newStartSkPt = vec2StartSkPt.subtract(
    dirVec.multiply(diff * 0.5 * skTurn, true),
    true
  );

  const dirVecFirst = createNormalizedVector(
    midSkPt.x - vec2StartSkPt.x,
    midSkPt.y - vec2StartSkPt.y
  );
  const dirVecSecond = createNormalizedVector(
    nextSkPt.x - midSkPt.x,
    nextSkPt.y - midSkPt.y
  );

  const intersectionPt = getInterceptPoint(
    newStartSkPt,
    dirVecFirst,
    nextSkPt,
    dirVecSecond,
    true
  );

  if (intersectionPt) {
    const newEndSkPt = intersectionPt;
    const updatedSkData = [...skData];

    updatedSkData.splice(startSkId, 1, { pos: newStartSkPt, depth });

    const nextIndex = startSkId === skLength - 1 ? startSkId - 1 : 1;

    updatedSkData.splice(nextIndex, 1, {
      pos: newEndSkPt,
      depth: startSkId === skLength - 1 ? depth : skData[nextIndex].depth,
    });

    return updatedSkData;
  } else {
    console.error('Error occurred during getting new skeleton point');

    return skData;
  }
};

const handleBoundaryEdge = (
  diff: number,
  skData: SkeletonSeg[],
  skId: number
): SkeletonSeg[] => {
  const skLength = skData.length;
  const currentPt = new Vec2(skData[skId].pos.x, skData[skId].pos.y);

  const nextSkPt =
    skId === 0
      ? new Vec2(skData[1].pos.x, skData[1].pos.y)
      : new Vec2(skData[skLength - 2].pos.x, skData[skLength - 2].pos.y);

  const dirVec = createNormalizedVector(
    currentPt.x - nextSkPt.x,
    currentPt.y - nextSkPt.y
  );

  const newPt = currentPt.add(dirVec.multiply(diff, true), true);
  const updatedSkData = [...skData];

  updatedSkData.splice(skId, 1, {
    pos: newPt,
    depth: skData[skId].depth,
  });

  return updatedSkData;
};

const handleMiddleEdge = (
  diff: number,
  skData: SkeletonSeg[],
  startSkId: number,
  endSkId: number,
  vec2StartSkPt: Vec2,
  vec2EndSkPt: Vec2
): SkeletonSeg[] => {
  const skLength = skData.length;
  const dirVec = createNormalizedVector(
    vec2EndSkPt.x - vec2StartSkPt.x,
    vec2EndSkPt.y - vec2StartSkPt.y
  );

  const updatedSkData = [...skData];

  if (
    isCorrectDirection(dirVec, DIRECTION.DOWN) ||
    isCorrectDirection(dirVec, DIRECTION.RIGHT)
  ) {
    const step = startSkId < endSkId ? 1 : -1;
    let i = endSkId;

    while (i >= 0 && i < skLength) {
      const newPt = new Vec2(skData[i].pos.x, skData[i].pos.y).add(
        dirVec.multiply(diff, true)
      );

      updatedSkData.splice(i, 1, {
        pos: newPt,
        depth: skData[i].depth,
      });
      i += step;
    }
  } else {
    const step = startSkId < endSkId ? -1 : 1;
    let i = startSkId;

    while (i >= 0 && i < skLength) {
      const newPt = new Vec2(skData[i].pos.x, skData[i].pos.y).add(
        dirVec.multiply(diff * -1, true)
      );

      updatedSkData.splice(i, 1, {
        pos: newPt,
        depth: skData[i].depth,
      });
      i += step;
    }
  }

  return updatedSkData;
};

export const updateShapeByEdge = (
  arrowInfo: ArrowInfo,
  shape: DrawingShape,
  whatIf: boolean
): void => {
  if (whatIf) {
    // skip the whatIf attempts for limit setting, kills performance
    return;
  }

  if (arrowInfo.isInterior) {
    handleInteriorArrow(arrowInfo, shape, whatIf);

    return;
  }

  if (!arrowInfo.drawingEdgeId) {
    return;
  }

  const { edge, arrow } = getEdgeAndArrow({
    arrowInfo: arrowInfo as Omit<ArrowInfo, 'drawingEdgeId'> & {
      drawingEdgeId: string;
    },
    shape,
  });

  if (
    !edge ||
    !arrow ||
    !arrowInfo.size ||
    !arrow.size ||
    arrowInfo.size === arrow.size
  ) {
    return;
  }

  const diff = arrowInfo.size - arrow.size;

  if (isBumpOut(edge)) {
    adjustBumpOutSides(diff, edge);
  }

  let skData = shape.skeleton.slice();
  const skLength = skData.length;

  const startSkId = shape.cornerMap[edge.startCornerId].skIndex;
  const endSkId = shape.cornerMap[edge.endCornerId].skIndex;
  const vec2StartSkPt = new Vec2(
    skData[startSkId].pos.x,
    skData[startSkId].pos.y
  );
  const vec2EndSkPt = new Vec2(skData[endSkId].pos.x, skData[endSkId].pos.y);
  const dirVec = edge.dirVec.normalize(true);

  if (skLength === 2 && startSkId === endSkId) {
    // This is for simple shape - Rectangle AND it's a change to an end edge
    // Got rid of half of the code that handles just a rectangle, it was not recreating vectors correctly
    // for side edges (non end edges)

    skData = handleSimpleRectangle({
      arrowInfoSize: arrowInfo.size,
      shape,
      edge,
      diff,
      skData,
      dirVec,
      vec2StartSkPt,
      vec2EndSkPt,
    });
  } else if (startSkId === endSkId) {
    //means the edge is one of two end edges
    skData = handleEndEdge(
      arrowInfo,
      shape,
      edge,
      diff,
      skData,
      startSkId,
      vec2StartSkPt,
      dirVec
    );
  } else if (startSkId === 0 || startSkId === skLength - 1) {
    skData = handleBoundaryEdge(diff, skData, startSkId);
  } else if (endSkId === 0 || endSkId === skLength - 1) {
    skData = handleBoundaryEdge(diff, skData, endSkId);
  } else {
    skData = handleMiddleEdge(
      diff,
      skData,
      startSkId,
      endSkId,
      vec2StartSkPt,
      vec2EndSkPt
    );
  }

  drawingTool.rebuildShapeFromNewSkeleton(skData, shape);
  if (!whatIf) {
    calculateEdgeConstraints(shape);
  }
};

export const updateApplianceCoords = (
  appliance: DrawingAppliance,
  canvas?: fabric.Canvas
) => {
  const applianceName = makeApplianceName(appliance);
  const fabricObj = canvas
    ?.getObjects()
    .find((obj) => obj.name === applianceName && obj.type === 'image') as
    | fabric.Image
    | undefined;

  if (fabricObj) {
    fabricObj.set({
      left: appliance.centerPoint.x,
      top: appliance.centerPoint.y,
      angle: appliance.angle,
    });
    fabricObj.setCoords();
  }
};

export const updateShapeByTranslation = (
  moveVec: Vec2,
  shape: DrawingShape,
  canvas?: fabric.Canvas
): void => {
  const skData = shape.skeleton.map((skSeg) => ({
    pos: subtractVectors(skSeg.pos, moveVec),
    depth: skSeg.depth,
  }));

  // move all the shape's appliances as well
  shape.appliances.forEach((appliance) => {
    appliance.centerPoint = subtractVectors(appliance.centerPoint, moveVec);

    updateApplianceCoords(appliance, canvas);
  });

  drawingTool.rebuildShapeFromNewSkeleton(skData, shape);
};

export const rotatePointAboutCenterPoint = (
  rotatingPoint: Vec2,
  centerPoint: Vec2,
  rotInc: number
): Vec2 => {
  const cos = Math.cos((rotInc * Math.PI) / 180);
  const sin = Math.sin((rotInc * Math.PI) / 180);
  const cpx = centerPoint.x;
  const cpy = centerPoint.y;
  const x = rotatingPoint.x;
  const y = rotatingPoint.y;

  return new Vec2(
    cos * (x - cpx) + (y - cpy) * sin + cpx,
    -sin * (x - cpx) + cos * (y - cpy) + cpy
  );
};

export const updateShapeByRotation = (
  canvas: fabric.Canvas | undefined,
  shape: DrawingShape,
  clockwise: boolean
): void => {
  const direction = clockwise ? -1 : 1;
  // called when a user rotates a shape
  const rotInc = SHAPE_ROTATION_DEGREES * direction;
  const objName =
    shape.canvasObjectName + CANVAS_OBJECT_TYPE_SUFFIX.SHAPE_GROUP;
  // find the canvas shape related to the current DrawingShape
  const [target] = (canvas?.getObjects() ?? []).filter(
    (obj) => obj.name === objName
  );
  // find center point to rotate the shape about
  const centerPoint = target?.getCenterPoint();
  const centerVec = centerPoint
    ? new Vec2(centerPoint.x, centerPoint.y)
    : new Vec2(0, 0);
  // get all skeleton points
  const skData = shape.skeleton.slice();
  const skLength = skData.length;

  // rotate each point in the skeleton
  range(0, skLength).forEach((i) => {
    skData[i].pos = rotatePointAboutCenterPoint(
      skData[i].pos,
      centerVec,
      rotInc
    );
  });

  // revolve all the shape's appliances about the shape's center
  shape.appliances.forEach((appliance) => {
    appliance.centerPoint = rotatePointAboutCenterPoint(
      appliance.centerPoint,
      centerVec,
      rotInc
    );
    // also rotate the sink image itself
    appliance.angle -= rotInc;

    // Force-sync the fabric image position and angle to the updated state
    // regardless of whether the appliance image is currently on canvas root
    // or inside a group. This prevents visual drift after rotation.
    updateApplianceCoords(appliance, canvas);
  });

  drawingTool.rebuildShapeFromNewSkeleton(skData, shape);
};
